import React, { useState, useEffect } from "react";
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Box,
  Chip,
} from "@mui/material";
import { db } from "../firebase-config";
import { collection, getDocs, query, where, or } from "firebase/firestore";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const Home = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  // Get query parameters from URL
  const searchQuery = searchParams.get("p") || "";
  const selectedTab = searchParams.get("tab") || "all";

  // Detect current user
  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
  }, []);

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const toolsRef = collection(db, "tools");
        let q;

        if (currentUser) {
          // Firestore OR query: Fetch tools that are public OR owned by the user
          q = query(toolsRef, or(where("isPublic", "==", true), where("ownerId", "==", currentUser.uid)));
        } else {
          // Fetch only public tools if not logged in
          q = query(toolsRef, where("isPublic", "==", true));
        }

        const snapshot = await getDocs(q);
        const toolsList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setTools(toolsList);
      } catch (error) {
        console.error("Error fetching tools:", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser !== undefined) {
      fetchTools();
    }
  }, [currentUser]);

  // Filter tools based on search input & selected tab
  const filteredTools = tools.filter((tool) => {
    const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = selectedTab === "all" || (tool.tags && tool.tags.includes(selectedTab));
    return matchesSearch && matchesTab;
  });

  // Function to update URL parameters
  const updateURL = (param, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(param, value);
    } else {
      newParams.delete(param);
    }
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Available Tools
      </Typography>

      {/* Search Field */}
      <TextField
        label="Search Tools"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={(e) => updateURL("p", e.target.value)}
      />

      {loading ? (
        <CircularProgress />
      ) : (
        <List>
          {filteredTools.length > 0 ? (
            filteredTools.map((tool) => (
              <ListItem key={tool.id} button component="a" href={`/tool/${tool.id}`}>
                <ListItemText
                  primary={tool.title}
                  secondary={tool.description ? tool.description.slice(0, 100) + "..." : "No description available"}
                />
                {/* Label: Public or Private */}
                {!tool.isPublic?<Chip
                  label={tool.isPublic ? "Public" : "Private"}
                  color={tool.isPublic ? "success" : "default"}
                  sx={{ marginLeft: "10px" }}
                />:null}
              </ListItem>
            ))
          ) : (
            <Typography variant="body1">No tools found.</Typography>
          )}
        </List>
      )}
    </Container>
  );
};

export default Home;
